import { useContext, useState } from 'react';
import { useMutation } from '@apollo/client/react';

import DeleteModal from '@atom/components/common/DeleteModal';
import TaskItemTile from '@atom/components/common/workOrderDetail/taskItemTile/TaskItemTile';
import WorkOrderContext from '@atom/components/workOrderDetail/WorkOrderContext';
import { TASK_UPDATE } from '@atom/graph/task';
import { Icon, IconButton, Snackbar } from '@atom/mui';
import { CloseIcon, CreateNewFolderIcon } from '@atom/mui/icons';
import { TaskCategory } from '@atom/types/task';

interface Props {
  category: TaskCategory;
  onClick: () => void;
}

const CategoryTile = ({ category, onClick }: Props) => {
  const { id, name } = category;
  const { workOrderDetail, task, refetch } = useContext(WorkOrderContext);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);

  const [updateTask, { loading }] = useMutation(TASK_UPDATE);

  const handleDeleteConfirm = async () => {
    const categories = task.categories.filter(
      ({ id: categoryId }) => categoryId !== id,
    );
    const categoryIds = categories.map(({ id: categoryId }) => categoryId);

    setDeleteModalOpen(false);

    Snackbar.info({
      message: 'Deleting...',
    });

    await updateTask({
      variables: {
        input: {
          id: task.id,
          workOrderId: workOrderDetail.id,
          categoryIds,
        },
      },
    });

    refetch();

    Snackbar.info({
      message: `Removed Folder.`,
    });
  };

  const handleDeleteClick = event => {
    event.stopPropagation();
    setDeleteModalOpen(true);
  };

  const icon = <Icon icon={CreateNewFolderIcon} />;
  const actionButton = (
    <IconButton
      size="small"
      onClick={handleDeleteClick}
      tooltip="Remove"
      data-cy={`categoryRemoveButton-${name}`}
    >
      <Icon icon={CloseIcon} />
    </IconButton>
  );

  return (
    <>
      <TaskItemTile
        content={name}
        tooltipContent={name}
        onTileClick={onClick}
        icon={icon}
        actionButton={actionButton}
        maxWidth="21.25rem"
        data-cy={`categoryTile-${name}`}
      />
      <DeleteModal
        open={deleteModalOpen}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteModalOpen(false)}
        loading={loading}
        title="Remove Folder"
        content="Are you sure you want to remove this folder?"
        confirmText="Remove"
      />
    </>
  );
};

export default CategoryTile;
